/**
 * Order rider cell — mirrors `fasto_staff`'s `RiderDetailBox` (see
 * `lib/features/order/presentation/components/order_rider_box.dart`).
 *
 * Renders, in a single inline pill:
 *
 *   - rider name (or `—` if unassigned)
 *   - a small bike icon when the rider is doing the trip on a cycle
 *   - a compact count badge equal to `batched_orders.length` when the
 *     rider is currently carrying multiple orders on the same trip.
 *     Tone follows the *active* leg of the batch — green if the leg
 *     including this row is still active, danger otherwise — matching
 *     the staff app's `checkIfOrderIsActive` heuristic.
 *
 * Clicking the pill opens a popover listing the sibling legs of the
 * batch with their status and timestamps, and each entry deep-links to
 * its own order detail page (`/orders/{id}`). For unassigned orders we
 * just render a muted dash so the column stays compact.
 */
import { Bike, History, List, Map as MapIcon, Phone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Popover } from '@/components/ui/Popover';
import { fmt } from '@/lib/format';
import { statusLabel, statusTone } from '@/lib/status';
import { cn } from '@/lib/cn';
import { BatchTripMap, LEG_COLORS } from '@/components/maps/BatchTripMap';
import type { BatchedOrderEntry, OrderRow } from '@/api/legacy/orders';
import { openRiderOrders } from '@/state/riderOrdersDialog';

interface OrderRiderCellProps {
  order: OrderRow;
}

function normalizeRiderName(raw: string | null): string {
  if (!raw) return '';
  return raw
    .replace(/[._-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function isLegActive(entry: BatchedOrderEntry): boolean {
  return entry.is_active && !entry.completed_timestamp && !entry.cancelled_timestamp;
}

function orderStoreId(order: OrderRow): number | null {
  const s = order.store_id;
  if (s == null) return null;
  const n = typeof s === 'number' ? s : Number(s);
  return Number.isFinite(n) ? n : null;
}

export function OrderRiderCell({ order }: OrderRiderCellProps) {
  const navigate = useNavigate();
  const name = normalizeRiderName(order.rider_name);
  const batch = Array.isArray(order.batched_orders) ? order.batched_orders : [];
  const batchCount = batch.length;
  const showBatchBadge = order.status !== 'cancelled' && batchCount > 1;

  const selfLeg = batch.find((b) => b.order_id === order.id);
  const tripActive = selfLeg ? isLegActive(selfLeg) : order.status !== 'completed' && order.status !== 'cancelled';

  if (!order.rider_id || !name) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const pill = (
    <button
      type="button"
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'press group relative inline-flex max-w-[180px] items-center gap-1 rounded-full',
        'border border-border bg-surface px-2 py-1 text-[11px] font-medium text-foreground',
        'hover:border-info/40 hover:text-info focus-ring',
      )}
      title={order.rider_phone ? `${name} · ${order.rider_phone}` : name}
    >
      {order.uses_cycle ? (
        <Bike className="h-3 w-3 shrink-0 text-info" aria-hidden />
      ) : null}
      <span className="truncate">{name}</span>
      {showBatchBadge ? (
        <span
          className={cn(
            'kpi-num ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none ring-1 ring-inset',
            tripActive
              ? 'bg-success/15 text-success ring-success/40'
              : 'bg-danger/15 text-danger ring-danger/40',
          )}
          aria-label={`${batchCount} orders in this trip`}
        >
          {batchCount}
        </span>
      ) : null}
    </button>
  );

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Popover
        side="bottom"
        align="start"
        trigger={pill}
        className="w-80 p-2"
      >
        <RiderPopoverContent
          riderId={order.rider_id}
          riderName={name}
          riderNameRaw={order.rider_name}
          riderPhone={order.rider_phone}
          storeId={orderStoreId(order)}
          usesCycle={!!order.uses_cycle}
          batch={batch}
          currentOrderId={order.id}
          onOrderClick={(id) => navigate(`/orders/${id}`)}
        />
      </Popover>
    </span>
  );
}

interface RiderPopoverContentProps {
  riderId: number;
  riderName: string;
  riderNameRaw: string | null;
  riderPhone: string | null;
  storeId: number | null;
  usesCycle: boolean;
  batch: BatchedOrderEntry[];
  currentOrderId: number;
  onOrderClick: (id: number) => void;
}

type PopoverView = 'list' | 'map';

function RiderPopoverContent({
  riderId,
  riderName,
  riderNameRaw,
  riderPhone,
  storeId,
  usesCycle,
  batch,
  currentOrderId,
  onOrderClick,
}: RiderPopoverContentProps) {
  function openHistory() {
    openRiderOrders({
      riderId,
      riderName: riderNameRaw ?? riderName,
      ...(storeId != null ? { storeId } : {}),
    });
  }

  const sorted = useMemo(
    () =>
      [...batch].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || a.order_id - b.order_id,
      ),
    [batch],
  );
  const showLegs = sorted.length > 1;
  const [view, setView] = useState<PopoverView>('list');

  // Always include the row's own order in the map fetch — even single-leg
  // trips benefit from seeing the customer marker + rider trail.
  const mapLegs = useMemo(() => {
    const list = sorted.length ? sorted : [];
    if (list.length === 0) {
      return [{ orderId: currentOrderId, index: 0 }];
    }
    return list.map((leg, i) => ({ orderId: leg.order_id, index: i }));
  }, [sorted, currentOrderId]);

  const legColorFor = (orderId: number) => {
    const idx = mapLegs.findIndex((l) => l.orderId === orderId);
    return idx >= 0 ? LEG_COLORS[idx % LEG_COLORS.length] : 'transparent';
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openHistory();
              }}
              className="truncate text-left text-xs font-semibold text-foreground underline-offset-2 hover:text-info hover:underline focus-ring rounded-sm"
              title="Ongoing and completed orders for this rider"
            >
              {riderName}
            </button>
            {usesCycle ? (
              <Bike className="h-3 w-3 text-info" aria-hidden />
            ) : null}
          </div>
          {riderPhone ? (
            <a
              href={`tel:${riderPhone}`}
              onClick={(e) => e.stopPropagation()}
              className="kpi-num inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <Phone className="h-3 w-3" /> {riderPhone}
            </a>
          ) : null}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openHistory();
            }}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-info hover:underline focus-ring rounded-sm"
          >
            <History className="h-3 w-3" aria-hidden />
            Rider order history
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <PopoverViewToggle value={view} onChange={setView} />
          {showLegs ? <Badge tone="info">{sorted.length}</Badge> : null}
        </div>
      </div>

      {view === 'map' ? (
        <BatchTripMap
          legs={mapLegs}
          currentOrderId={currentOrderId}
          height={220}
          className="overflow-hidden rounded-md border border-border"
        />
      ) : null}

      {!showLegs && view === 'list' ? (
        <p className="px-1 text-[11px] text-muted-foreground">
          Single-order trip — no other legs assigned.
        </p>
      ) : null}

      {showLegs || view === 'map' ? (
        <ul className="max-h-56 space-y-1 overflow-y-auto pr-0.5">
          {(showLegs
            ? sorted
            : [
                {
                  order_id: currentOrderId,
                  status: null,
                  completed_timestamp: null,
                  cancelled_timestamp: null,
                  cancellation_reason: null,
                  is_active: true,
                  sequence: 0,
                } as BatchedOrderEntry,
              ]
          ).map((leg) => {
            const ts =
              leg.cancelled_timestamp ?? leg.completed_timestamp ?? null;
            const isCurrent = leg.order_id === currentOrderId;
            const dotColor = view === 'map' ? legColorFor(leg.order_id) : null;
            return (
              <li key={leg.order_id}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrderClick(leg.order_id);
                  }}
                  className={cn(
                    'press flex w-full items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left',
                    'hover:bg-muted/50',
                    isCurrent
                      ? 'border-primary/40 bg-primary/5'
                      : 'border-border bg-surface',
                  )}
                >
                  <div className="flex min-w-0 items-center gap-1.5">
                    {dotColor ? (
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-white"
                        style={{ backgroundColor: dotColor }}
                        aria-hidden
                      />
                    ) : null}
                    <div className="flex min-w-0 flex-col">
                      <span className="kpi-num text-[11px] font-medium text-foreground">
                        #{leg.order_id}
                        {isCurrent ? (
                          <span className="ml-1 rounded bg-primary/10 px-1 text-[10px] font-medium text-primary">
                            this
                          </span>
                        ) : null}
                      </span>
                      {ts ? (
                        <span className="text-[10px] text-muted-foreground">
                          {fmt.datetime(ts)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {leg.status ? (
                    <Badge tone={statusTone(leg.status)}>
                      {statusLabel(leg.status)}
                    </Badge>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function PopoverViewToggle({
  value,
  onChange,
}: {
  value: PopoverView;
  onChange: (next: PopoverView) => void;
}) {
  const items: Array<{ key: PopoverView; label: string; icon: typeof List }> = [
    { key: 'list', label: 'List', icon: List },
    { key: 'map', label: 'Map', icon: MapIcon },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Toggle list or map view"
      className="inline-flex items-center rounded-full border border-border bg-surface p-0.5"
    >
      {items.map(({ key, label, icon: Icon }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={(e) => {
              e.stopPropagation();
              onChange(key);
            }}
            className={cn(
              'press inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium focus-ring transition-colors',
              active
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
            title={label}
          >
            <Icon className="h-3 w-3" aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}
